import { configs } from '@/configs/configs';
import { MAX_PAGINATION_LIMIT } from '@/features/Admin/OptimizationNotes/constants/defaultUrlStateValues';
import { accessTokenInterceptor, addAuthRefreshInterceptorToAxios } from 'auth';
import axios from 'axios';
import dayjs from 'dayjs';

export const BASE_URL = configs.NEXT_PUBLIC_BACKEND_URL;

const axiosInstance = axios.create({
  baseURL: BASE_URL,
});

axiosInstance.interceptors.request.use(accessTokenInterceptor);
addAuthRefreshInterceptorToAxios(axiosInstance);

export const fetcher = async (url: string) => {
  const response = await axiosInstance.get(url);
  return response.data;
};
export const post = async (url: string, body: unknown) => {
  const response = await axiosInstance.post(url, body);
  return response.data;
};
export const patch = async (url: string, body: unknown) => {
  const response = await axiosInstance.patch(url, body);
  return response.data;
};
export const put = async (url: string, body: unknown) => {
  const response = await axiosInstance.put(url, body);
  return response.data;
};
export const del = async (url: string) => {
  const response = await axiosInstance.delete(url);
  return response.data;
};

export const postFile = async <T = unknown>(url: string, file: File): Promise<T> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosInstance.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const terminator = async (url: string) => {
  const response = await axiosInstance.delete(url);
  return response.data;
};

export const REQUEST_URLS = {
  FETCH_USER_PROPERTIES: (params: { page: string; limit: string }) =>
    `${BASE_URL}/userProperties?page=${params.page}&limit=${params.limit}`,
  FETCH_USER_PROPERTY: (params: { propertyId: string }) =>
    `${BASE_URL}/userProperties/${params.propertyId}`,
  FETCH_PROPERTY_POSTS: (params: { propertyId: string; search: string }) =>
    `${BASE_URL}/userProperties/${params.propertyId}/gbp-posts?s=${encodeURIComponent(params.search)}`,
  FETCH_PROPERTY_IMAGES: (params: { propertyId: string }) =>
    `${BASE_URL}/v1/userProperties/${params.propertyId}/images`,
  FETCH_PROPERTY_PRODUCTS: (params: { propertyId: string }) =>
    `${BASE_URL}/userProperties/${params.propertyId}/gbp-product-posts`,
  FETCH_ACCOUNT_PROPERTY_POSTS: (params: { accountPropertyId: string; page; limit }) =>
    `${BASE_URL}/v1/posts?filter.configuration.accountPropertyId=${params.accountPropertyId}&page=${params.page}&limit=${params.limit}`,
  FETCH_USER_PMCS: (params: { search: { pmc: string } }) =>
    `${BASE_URL}/userProperties/pmcs?s=${encodeURIComponent(JSON.stringify(params.search))}`,
  FETCH_USER_PROPERTY_ASSIGNED_AGENTS: (params: { search: { assignedAgents: string } }) =>
    `${BASE_URL}/userProperties/assignedAgents?s=${encodeURIComponent(JSON.stringify(params.search))}`,
  FETCH_ACCOUNT_MANAGEMENT_COMPANIES: (params: { search: string; limit?: number }) =>
    `${BASE_URL}/v1/account-property-management-companies?search=${encodeURIComponent(params.search)}&limit=${params.limit || 10}`,
  FETCH_ACCOUNT_MANAGEMENT_COMPANY_BY_ID: (id: string) =>
    `${BASE_URL}/v1/account-property-management-companies/${id}`,
  FETCH_ACCOUNT_PROPERTY: (filterByAccountPropertyId: string) =>
    `${BASE_URL}/v1/posts-configurations?${filterByAccountPropertyId}`,
  FETCH_ACCOUNT_PROPERTY_IMAGES: (accountPropertyId: string, page, limit, sortBy, filter) => {
    let url = `${BASE_URL}/v1/media/${accountPropertyId}?page=${page}&limit=${limit}&sortBy=${sortBy}`;
    if (filter) {
      url += `&filter.${filter}`;
    }
    return url;
  },
  FETCH_ACCOUNT_PROPERTY_SCHEDULED_POSTS: (params: {
    accountPropertyId: string;
    page: number;
    limit: number;
    sortBy: string;
  }) =>
    `${BASE_URL}/v1/posts/scheduled-posts/${params.accountPropertyId}?page=${params.page}&limit=${params.limit}&sortBy=${params.sortBy}`,
  FETCH_ACCOUNT_PROPERTY_PRODUCTS: (accountPropertyId: string) =>
    `${BASE_URL}/v1/posts/products/${accountPropertyId}`,

  FETCH_ACCOUNT_PROPERTY_SOCIAL_CHANNELS_CONFIGURATIONS: (accountPropertyId: string) =>
    `${BASE_URL}/v1/account-properties/${accountPropertyId}/post-channel-configurations`,
  FETCH_GBP_ONBOARDING_URL: () => `${BASE_URL}/v1/google-locations/activateAccount`,
  UPLOAD_GBP_USER_TOKEN: () => `${BASE_URL}/v1/google-locations/onboardAccount`,
  FETCH_ACCOUNT_PROPERTY_GOOGLE_LOCATION_CONFIGURATION: (accountPropertyId: string) =>
    `${BASE_URL}/v1/google-locations/${accountPropertyId}`,
  UPDATE_ACCOUNT_PROPERTY_GOOGLE_LOCATION_CONFIGURATION: (googleConfigId: string) =>
    `${BASE_URL}/v1/google-locations/${googleConfigId}`,
  SEARCH_ACCOUNT_PROPERTY_GOOGLE_LOCATION_CONFIGURATION: (params: {
    search: string;
    excludeAssigned: boolean;
    page?: number;
    limit?: number;
  }) =>
    `${BASE_URL}/v1/google-locations?search=${encodeURIComponent(params.search)}&page=${params.page || 1}&limit=${params.limit || 4}${params.excludeAssigned ? `&filter.accountPropertyId=$null` : ''}`,
  FETCH_ACCOUNT_PROPERTY_ASSIGNED_TO: () => `${BASE_URL}/v1/posts-configurations/filters`,
  FETCH_POSTS_ERRORS_REPORT: (
    params: {
      startDate: string;
      endDate: string;
      search?: string;
      page?: number;
      limit?: number;
      sortBy?: string;
      pmcId?: string;
    },
    csv: boolean = false,
  ) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) {
      queryParams.append('filter.startDate', `$eq:${params.startDate}`);
    }
    if (params.endDate) {
      queryParams.append('filter.endDate', `$eq:${params.endDate}`);
    }

    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params.pmcId) {
      queryParams.append(
        'filter.accountProperty.accountPropertyManagementCompanyId',
        `$eq:${params.pmcId}`,
      );
    }
    return `${BASE_URL}/v1/posts-errors-report${csv ? '/csv' : ''}?${queryParams.toString()}`;
  },
  FETCH_MY_PROPERTIES_CONFIGS: (params: {
    pagination: { currentPage: number; itemsPerPage: number };
    filterByAccountPropertyId: string;
    filterByCompanyId?: string;
    filterByAssignedTo?: string;
    search?: string;
    searchBy?: string;
  }) =>
    `${BASE_URL}/v1/posts-configurations?page=${params.pagination?.currentPage}&limit=${params.pagination.itemsPerPage}${params.filterByAccountPropertyId}` +
    (params.filterByCompanyId ? params.filterByCompanyId : '') +
    (params.filterByAssignedTo ? params.filterByAssignedTo : '') +
    (params.search ? `&search=${encodeURIComponent(params.search)}` : '') +
    (params.searchBy ? `&searchBy=${params.searchBy}` : ''),

  CREATE_ACCOUNT_PROPERTY_SOCIAL_CHANNEL_CONFIGURATION: (accountPropertyId: string) =>
    `${BASE_URL}/v1/account-properties/${accountPropertyId}/post-channel-configurations/defaultSettings`,
  UPDATE_ACCOUNT_PROPERTY_SOCIAL_CHANNEL_CONFIGURATION: (
    configId: string,
    accountPropertyId: string,
  ) =>
    `${BASE_URL}/v1/account-properties/${accountPropertyId}/post-channel-configurations/${configId}`,
  FETCH_ACCOUNT_PROPERTY_SOCIAL_CHANNEL_SCHEDULE_CADENCES: (
    accountPropertyId: string,
    configId: string,
  ) =>
    `${BASE_URL}/v1/account-properties/${accountPropertyId}/post-channel-configurations/${configId}/schedule-cadences`,
  UPDATE_ACCOUNT_PROPERTY_SOCIAL_CHANNEL_SCHEDULE_CADENCES: (
    configId: string,
    accountPropertyId: string,
  ) =>
    `${BASE_URL}/v1/account-properties/${accountPropertyId}/post-channel-configurations/${configId}/schedule-cadences`,
  UPDATE_ACCOUNT_PROPERTY_CONFIG: accountPropertyId =>
    `${BASE_URL}/v1/posts-configurations/${accountPropertyId}`,
  ADMIN_FETCH_ACCOUNT_PROPERTIES_FOR_ACCOUNT_USER: (
    accountUserId: string,
    queryParams: string = '',
  ) => `${BASE_URL}/v1/admin/account-users/${accountUserId}/account-properties${queryParams}`,
  ADMIN_CREATE_ACCOUNT_USER: `${BASE_URL}/v1/admin/account-users`,
  ADMIN_ACCOUNT_USERS: (queryParams: string = '') =>
    `${BASE_URL}/v1/admin/account-users${queryParams}`,
  ACCOUNT_USERS: (queryParams: string = '') => `${BASE_URL}/v1/account-users${queryParams}`,
  ADMIN_DELETE_ACCOUNT_USER: (accountUserId: string) =>
    `${BASE_URL}/v1/admin/account-users/${accountUserId}`,
  ADMIN_ACCOUNT_PROPERTIES: `${BASE_URL}/v1/admin/account-properties`,
  ADMIN_ACCOUNT_PMCS: `${BASE_URL}/v1/admin/account-pmcs`,
  ADMIN_ALL_USERS: (queryParams: string = '') => `${BASE_URL}/v1/admin/users${queryParams}`,
  ADMIN_USER: (userId: string) => `${BASE_URL}/v1/admin/users/${userId}`,
  ADMIN_ALL_ACCOUNTS: () => `${BASE_URL}/v1/admin/accounts`,
  ADMIN_ACCOUNT_USERS_ACCESS: `${BASE_URL}/v1/admin/account-users-access`,
  ADMIN_ACCOUNT_USERS_ACCOUNT_USERS_ACCESS: (accountUserId: string, id: string) =>
    `${BASE_URL}/v1/admin/account-users/${accountUserId}/account-users-access/${id}`,
  UPDATE_POST: (postId: string) => `${BASE_URL}/v1/posts/${postId}`,
  RETRY_POST: (postId: string) => `${BASE_URL}/v1/posts/${postId}/retry`,
  REGENERATE_ACCOUNT_PROPERTY_SOCIAL_POSTS: (accountPropertyId: string) =>
    `${BASE_URL}/v1/posts/regenerate/${accountPropertyId}`,
  UPLOAD_MEDIA: `${BASE_URL}/v1/media/upload`,
  AGENCY_ONBOARDING: (queryParams: string = '') =>
    `${BASE_URL}/v1/agency-onboardings/${queryParams}`,
  SYNC_AGENCY_ONBOARDING_TO_SALESFORCE: (agencyOnboardingId: string) =>
    `${BASE_URL}/v1/agency-onboardings/${agencyOnboardingId}/sync`,
  GET_SHEET_NAMES_FROM_GSHEET: `${BASE_URL}/v1/agency-onboardings/sheet-names`,
  ONBOARD_ACCOUNT_PROPERTIES_FROM_GSHEET: `${BASE_URL}/v1/agency-onboardings`,
  FETCH_ALL_TAGS: `${BASE_URL}/v1/tags`,
  FETCH_TAG_BY_NAME: (tag: string) => `${BASE_URL}/v1/tags/${tag}`,
  UPDATE_MEDIA_TAGS: (mediaId: string) => `${BASE_URL}/v1/media/${mediaId}/tags`,
  FETCH_IMAGE_REPORT_CSV: (params: { sortBy?: string; search?: string; filterPmcId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params.search) {
      queryParams.append('filter.propertyName', `$ilike:${params.search}`);
    }
    if (params.filterPmcId) {
      queryParams.append(
        'filter.accountProperty.accountPropertyManagementCompanyId',
        `$eq:${params.filterPmcId}`,
      );
    }
    return `${BASE_URL}/v1/media/image-report/csv?${queryParams.toString()}`;
  },
  HAS_SALESFORCE_CHANGED_SINCE_LAST_SYNC: (agencyOnboardingId: string) =>
    `${BASE_URL}/v1/agency-onboardings/${agencyOnboardingId}/changed-since-last-sync`,
  INVALID_CONNECTION_CHECKS: (queryParams: string) =>
    `${BASE_URL}/v1/connection-checks/failures${queryParams}`,
  UPDATE_SHOULD_NOTIFY: (ccId: string) => `${BASE_URL}/v1/connection-checks/${ccId}`,
  RECHECK_CONNECTIONS: () => `${BASE_URL}/v1/connection-checks/recheck`,
  ARCHIVE_AGENCY_ONBOARDING: (id: string) => `${BASE_URL}/v1/agency-onboardings/${id}/archive`,
  FETCH_GBP_AUTHORIZED_EMAILS: () => `${BASE_URL}/v1/gbp-user-tokens/list`,
  FETCH_PROPERTY_SENT_NOTIFICATIONS: (accountPropertyId: string) =>
    `${BASE_URL}/v1/notification-subscriptions/sent/account-property/${accountPropertyId}`,
  FETCH_ACCOUNT_PROPERTY_TASKS: (
    accountPropertyIds: string[],
    options?: { limit?: number; createdAtBtw?: `${string},${string}`; statuses?: string[] },
  ) => {
    const { limit = MAX_PAGINATION_LIMIT, createdAtBtw, statuses = [] } = options ?? {};
    const params = new URLSearchParams();
    params.set('filter.accountPropertyId', `$in:${accountPropertyIds.join(',')}`);
    params.set('limit', limit.toString());
    if (createdAtBtw) {
      params.set('filter.createdAt', `$btw:${createdAtBtw}`);
    }
    if (statuses.length) {
      params.set('filter.status', `$in:${statuses.join(',')}`);
    }
    return `${BASE_URL}/v1/account-property-tasks?${params.toString()}`;
  },
  FETCH_ACCOUNT_PROPERTY_TASKS_BY_ACCOUNT_PROPERTY: (queryParams: string = '') =>
    `${BASE_URL}/v1/account-property-tasks/by-account-property${queryParams}`,
  CREATE_ACCOUNT_PROPERTY_TASK: `${BASE_URL}/v1/account-property-tasks`,
  UPDATE_ACCOUNT_PROPERTY_TASK_STATUS: (taskId: string) =>
    `${BASE_URL}/v1/account-property-tasks/${taskId}/update-status`,
  FETCH_RECENT_ACCOUNT_PROPERTIES_NOTES: (
    accountPropertyIds: string[],
    options?: { limit?: number },
  ) => {
    // TODO: AEP-162
    const params = new URLSearchParams();
    params.set('filter.id', `$in:${accountPropertyIds.join(',')}`);
    params.set('limit', options?.limit?.toString() ?? MAX_PAGINATION_LIMIT.toString());
    return `${BASE_URL}/v1/account-property-notes/recent/by-account-property?${params.toString()}`;
  },
  FETCH_HISTORICAL_ACCOUNT_PROPERTY_NOTES: (
    accountPropertyId: string,
    options?: {
      dateRange?: { from: Date; to: Date };
      createdByAccountUserIds?: string[];
      labelNames?: string[];
      excludeLabelNames?: string[];
      noteIds?: string[];
      tagResourceIds?: string[];
      limit?: number;
    },
  ) => {
    let url = `${BASE_URL}/v1/account-property-notes?filter.accountPropertyId=$eq:${accountPropertyId}`;
    if (options?.dateRange?.from && options?.dateRange?.to) {
      const startOfDay = dayjs(options.dateRange.from).startOf('day').toISOString();
      const endOfDay = dayjs(options.dateRange.to).endOf('day').toISOString();
      url += `&filter.createdAt=$btw:${startOfDay},${endOfDay}`;
    }
    if (options?.createdByAccountUserIds && options.createdByAccountUserIds.length > 0) {
      url += `&filter.createdByAccountUserId=$in:${options.createdByAccountUserIds.join(',')}`;
    }
    if (options?.labelNames && options.labelNames.length > 0) {
      url += `&filter.labels.name=$in:${options.labelNames.join(',')}`;
    }
    if (options?.excludeLabelNames && options.excludeLabelNames.length > 0) {
      url += `&filter.labels.name=$not:$in:${options.excludeLabelNames.join(',')}`;
    }
    if (options?.noteIds && options.noteIds.length > 0) {
      url += `&filter.id=$in:${options.noteIds.join(',')}`;
    }
    if (options?.tagResourceIds && options.tagResourceIds.length > 0) {
      url += `&filter.tags.resourceId=$in:${options.tagResourceIds.join(',')}`;
    }
    if (options?.limit) {
      url += `&limit=${options.limit}`;
    }
    return url;
  },
  CREATE_ACCOUNT_PROPERTY_NOTE: `${BASE_URL}/v1/account-property-notes`,
  FETCH_CONNECTION_CHECKS: (accountPropertyIds: string[], limit: number = 250) => {
    const params = new URLSearchParams();
    params.set('filter.accountPropertyId', `$in:${accountPropertyIds.join(',')}`);
    params.set('limit', limit.toString());
    return `${BASE_URL}/v1/connection-checks?${params.toString()}`;
  },
  FETCH_CONNECTION_CHECKS_BY_ACCOUNT_PROPERTY: (queryParams: string = '') =>
    `${BASE_URL}/v1/connection-checks/by-account-property${queryParams}`,
  FETCH_ACCOUNT_PROPERTIES: (queryParams: string = '') =>
    `${BASE_URL}/v1/account-properties${queryParams}`,
  FETCH_OPTIMIZATION_NOTES_ACCOUNT_PROPERTIES: (queryParams: string = '') =>
    `${BASE_URL}/v1/optimization-notes/account-properties${queryParams}`,
  FETCH_LATEST_AD_PERFORMANCE_CRITERIA: (accountPropertyId: string) => {
    return `${BASE_URL}/v1/account-property-ad-performance-criteria-result/latest/by-account-property?filter.id=$in:${accountPropertyId}`;
  },
  FETCH_AD_PERFORMANCE_CRITERIA_BY_BATCH: (accountPropertyId: string, batchId: string) => {
    return `${BASE_URL}/v1/account-property-ad-performance-criteria-result/by-account-property?filter.id=$in:${accountPropertyId}&filter.adPerformanceCriteriaResults.batchId=$in:${batchId}`;
  },
  FETCH_FUNNEL: (params: {
    accountPropertyId: string;
    adMonthDate?: string;
    source?: 'stub' | 'snowflake' | 'postgres';
  }) => {
    const queryParams = new URLSearchParams();
    if (params.adMonthDate) {
      queryParams.append('adMonthDate', params.adMonthDate);
    }
    if (params.source) {
      queryParams.append('source', params.source);
    }
    const path = `${BASE_URL}/v1/fiona-reports/funnel/${params.accountPropertyId}`;
    return queryParams.toString() ? `${path}?${queryParams.toString()}` : path;
  },
  FETCH_PMC_OCCUPANCY: (params: {
    pmcId: string;
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    adMonthDate?: string;
    accountPropertyId?: string;
    sortBy?: string;
    responsibleAccountUserId?: string;
    states?: string[];
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append(
      'filter.accountProperty.accountPropertyManagementCompanyId',
      `$eq:${params.pmcId}`,
    );
    if (params.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.status) {
      queryParams.append('status', params.status);
    }
    if (params.adMonthDate) {
      queryParams.append('filter.adMonthDate', `$eq:${params.adMonthDate}`);
    } else if (params.startDate && params.endDate) {
      queryParams.append('filter.adDate', `$btw:${params.startDate},${params.endDate}`);
    } else if (params.startDate) {
      queryParams.append('filter.adDate', `$gte:${params.startDate}`);
    } else if (params.endDate) {
      queryParams.append('filter.adDate', `$lte:${params.endDate}`);
    }
    if (params.accountPropertyId) {
      queryParams.append('filter.accountPropertyId', `$eq:${params.accountPropertyId}`);
    }
    if (params.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params.responsibleAccountUserId) {
      queryParams.append(
        'filter.accountProperty.responsibleUsers.accountUserId',
        `$eq:${params.responsibleAccountUserId}`,
      );
      queryParams.append(
        'filter.accountProperty.responsibleUsers.responsibility',
        '$eq:accountManager',
      );
    }
    if (params.states?.length) {
      queryParams.append('filter.accountProperty.state', `$in:${params.states.join(',')}`);
    }
    const path = `${BASE_URL}/v1/fiona-reports/occupancy/pmc`;
    return queryParams.toString() ? `${path}?${queryParams.toString()}` : path;
  },
  FETCH_PMC_OCCUPANCY_SUMMARY: (params: {
    pmcId: string;
    adMonthDate?: string;
    responsibleAccountUserId?: string;
    states?: string[];
  }) => {
    const qs = new URLSearchParams({ pmcId: params.pmcId });
    if (params.adMonthDate) qs.append('adMonthDate', params.adMonthDate);
    if (params.responsibleAccountUserId)
      qs.append('responsibleAccountUserId', params.responsibleAccountUserId);
    if (params.states?.length) qs.append('states', params.states.join(','));
    return `${BASE_URL}/v1/fiona-reports/occupancy/pmc/summary?${qs.toString()}`;
  },
  FETCH_PMC_STATES: (pmcId: string) =>
    `${BASE_URL}/v1/account-property-management-companies/${pmcId}/states`,
  ADMIN_CREATE_API_KEY: `${BASE_URL}/v1/admin/api-keys`,
  ADMIN_DELETE_API_KEY: (id: string) => `${BASE_URL}/v1/admin/api-keys/${id}`,
  ADMIN_GET_USER_API_KEYS: (userId: string) => `${BASE_URL}/v1/admin/users/${userId}/api-keys`,
  ADMIN_FEATURE_FLAGS: (queryParams: string = '') =>
    `${BASE_URL}/v1/admin/feature-flags${queryParams}`,
  ADMIN_FEATURE_FLAG: (id: string) => `${BASE_URL}/v1/admin/feature-flags/${id}`,
  ADMIN_CREATE_FEATURE_FLAG: `${BASE_URL}/v1/admin/feature-flags`,
  ADMIN_UPDATE_FEATURE_FLAG: (id: string) => `${BASE_URL}/v1/admin/feature-flags/${id}`,
  ADMIN_DELETE_FEATURE_FLAG: (id: string) => `${BASE_URL}/v1/admin/feature-flags/${id}`,
  ADMIN_ACCOUNT_USER_FEATURE_FLAGS: (accountUserId: string) =>
    `${BASE_URL}/v1/admin/feature-flags/account-users/${accountUserId}`,
  ADMIN_ASSIGN_ACCOUNT_USER_FEATURE_FLAG: (featureFlagId: string) =>
    `${BASE_URL}/v1/admin/feature-flags/${featureFlagId}/account-users`,
  ADMIN_REMOVE_ACCOUNT_USER_FEATURE_FLAG: (featureFlagId: string, accountUserId: string) =>
    `${BASE_URL}/v1/admin/feature-flags/${featureFlagId}/account-users/${accountUserId}`,
  BULK_VIOLATIONS_STATUS_PREVIEW: `${BASE_URL}/v1/violations/bulk-status/preview`,
  BULK_RESOLVE_VIOLATIONS_EXECUTE: `${BASE_URL}/v1/violations/bulk-resolve/execute`,
  BULK_DISMISS_VIOLATIONS_EXECUTE: `${BASE_URL}/v1/violations/bulk-dismiss/execute`,
};
